import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Skeleton
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'products'));
      const productsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setProducts(productsData);
      setLoading(false);
    } catch (error) {
      console.error('Ürünler yüklenirken hata:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleProductClick = (productId) => {
    navigate(`/product/${productId}`);
  };

  const filteredProducts = products.filter(product =>
    product.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Container maxWidth="lg" sx={{ 
      py: 4,
      mt: { xs: '64px', sm: '80px' },
      minHeight: 'calc(100vh - 64px)',
      mb: 4
    }}>
      <Box sx={{ mb: 4 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Ürün ara..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ 
            '& .MuiOutlinedInput-root': {
              borderRadius: { xs: 1, sm: 2 }
            }
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {loading ? (
          // Loading skeletons
          [1, 2, 3, 4, 5, 6, 8].map((item) => (
            <Grid item key={item} xs={12} sm={6} md={4} lg={3}>
              <Card>
                <Skeleton variant="rectangular" height={300} />
                <CardContent>
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width="60%" />
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          // Actual products
          filteredProducts.map((product) => (
            <Grid item key={product.id} xs={12} sm={6} md={4} lg={3}>
              <Card 
                onClick={() => handleProductClick(product.id)}
                sx={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  borderRadius: 2,
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                    '& .product-image': {
                      transform: 'scale(1.05)'
                    }
                  }
                }}
              >
                {/* Stok durumu badge */}
                {product.stock <= 5 && product.stock > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      bgcolor: 'error.main',
                      color: 'white',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      zIndex: 1,
                      boxShadow: 2
                    }}
                  >
                    Son {product.stock} ürün
                  </Box>
                )}

                {/* Ürün görseli */}
                <Box sx={{ position: 'relative', pt: '133%', overflow: 'hidden' }}>
                  <CardMedia
                    component="img"
                    image={product.images?.[0] || '/images/placeholder.jpg'}
                    alt={product.title}
                    className="product-image"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      transition: 'transform 0.3s ease'
                    }}
                  />
                </Box>
                
                {/* Ürün detayları */}
                <CardContent sx={{ flexGrow: 1, p: 2, bgcolor: 'background.paper' }}>
                  {/* Kategori */}
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 1,
                      display: 'block'
                    }}
                  >
                    {product.category?.replace('-', ' ')}
                  </Typography>

                  {/* Başlık */}
                  <Typography 
                    variant="h6" 
                    component="h2" 
                    gutterBottom
                    sx={{ 
                      fontSize: '1.1rem',
                      fontWeight: 500,
                      mb: 1,
                      height: '2.4em',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {product.title}
                  </Typography>

                  {/* Fiyat ve stok durumu */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 'auto'
                  }}>
                    <Typography 
                      variant="h6" 
                      color="primary"
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '1.2rem'
                      }}
                    >
                      {product.price?.toLocaleString('tr-TR')} TL
                    </Typography>

                    {/* Stok durumu */}
                    <Typography 
                      variant="caption"
                      sx={{ 
                        color: product.stock > 0 ? 'success.main' : 'error.main',
                        fontWeight: 500
                      }}
                    >
                      {product.stock > 0 ? 'Stokta' : 'Tükendi'}
                    </Typography>
                  </Box>

                  {/* Beden bilgisi */}
                  {product.sizes && (
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 0.5, 
                      mt: 1,
                      flexWrap: 'wrap'
                    }}>
                      {product.sizes.map((size) => (
                        <Typography
                          key={size}
                          variant="caption"
                          sx={{
                            px: 1,
                            py: 0.5,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            fontSize: '0.7rem'
                          }}
                        >
                          {size}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))
        )}
      </Grid>

      {error && (
        <Box sx={{ textAlign: 'center', mt: 4, color: 'error.main' }}>
          <Typography>{error}</Typography>
        </Box>
      )}

      {!loading && filteredProducts.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography>Ürün bulunamadı.</Typography>
        </Box>
      )}
    </Container>
  );
}

export default Products; 